import { Controller } from '@hotwired/stimulus';

/**
 * Magic Link Auto-Submit Controller
 *
 * Submits the magic-link confirmation form on connect so real users
 * experience single-click login.
 *
 * The server only attaches this controller in a RECOGNIZED browser
 * (ClientPortal::RecognizedBrowser: the browser that requested the link,
 * or one where this client already signed in). Mail gateways such as
 * Mimecast and Microsoft Defender DO execute JS: they open the link in a
 * fresh browser from rotating datacenter IPs, so they never carry our
 * cookie, never get this controller, and see the manual button instead.
 * The token survives until the person clicks (incident 2026-10-19).
 *
 * Even in a recognized browser, the form is only submitted once the page
 * is actually shown: a long-press preview (Safari, iOS Mail) or a Chrome
 * prerender would otherwise burn the token before the real visit.
 *
 * Guards against re-submission on Turbo reconnection and ensures the
 * element is a form.
 *
 * Attach with: data-controller="magic-link-auto-submit"
 * Works only on <form> elements. Uses requestSubmit() so HTML5
 * validation runs (harmless here — the form has only hidden fields).
 */
export default class extends Controller {
  connect() {
    if (this.element.tagName !== 'FORM') return;
    if (this.#alreadySubmitted()) return;

    if (this.#isShown()) {
      this.#scheduleSubmit();
      return;
    }

    this.onShown = this.handleShown.bind(this);
    document.addEventListener('visibilitychange', this.onShown);
    document.addEventListener('prerenderingchange', this.onShown);
  }

  disconnect() {
    this.#stopListening();
  }

  handleShown() {
    if (!this.#isShown()) return;

    this.#stopListening();
    this.#scheduleSubmit();
  }

  #isShown() {
    return document.visibilityState === 'visible' && !document.prerendering;
  }

  // Fire on the next animation frame so the "Connexion en cours…" UI
  // paints before the POST and redirect — users see intent before navigation.
  #scheduleSubmit() {
    requestAnimationFrame(() => this.#submitOnce());
  }

  #stopListening() {
    if (!this.onShown) return;

    document.removeEventListener('visibilitychange', this.onShown);
    document.removeEventListener('prerenderingchange', this.onShown);
    this.onShown = null;
  }

  #submitOnce() {
    if (this.#alreadySubmitted()) return;
    this.element.dataset.magicLinkSubmitted = 'true';
    this.element.requestSubmit();
  }

  #alreadySubmitted() {
    return this.element.dataset.magicLinkSubmitted === 'true';
  }
}
